import { Button, tokens } from "@blurb/codex-react";
import { createStyles, SelectItem, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL, useRawGraphqlRequest } from "@rpi/openapi-api";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { SearchInputs } from "../../utils/graphql.util";

export const path = "filter";
const NO_FILTER_VALUE = "All";

const useFilterStyles = createStyles({
  root: {
    padding: `0 ${tokens.spacing[6]}`,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[6],
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: tokens.spacing[6],
    gap: tokens.spacing[2],
  },
});

type CustomerProductFilterProps = {
  isOpened: boolean;
  filters: SearchInputs<GraphQL.CustomerProductsQueryVariables>;
  onClose: () => void;
  onApply: (filter: SearchInputs<GraphQL.CustomerProductsQueryVariables>) => void;
};
export function CustomerProductFilter({ isOpened, filters, onClose, onApply }: CustomerProductFilterProps) {
  const { classes } = useFilterStyles();
  const organizations = useAllCustomers();

  const outletContext: InnerProps = {
    filters: filters,
    organizations,
    onApply,
    onClose,
  };

  const filterFormId = "customer-product-filter-form";

  return (
    <RpaDrawer title="Filters" opened={isOpened} onClose={onClose}>
      <Outlet context={outletContext} />
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button variant="filled" fullWidth intent="primary" type="submit" form={filterFormId}>
            Apply
          </Button>
          <Button variant="text" intent="primary" type="reset" form={filterFormId}>
            Clear All
          </Button>
        </div>
      </RpaDrawer.Footer>
    </RpaDrawer>
  );
}

type InnerProps = {
  filters: SearchInputs<GraphQL.CustomerProductsQueryVariables>;
  organizations?: GraphQL.AdminSearchCustomersForFilterQuery["adminSearchCustomers"]["data"];
  onClose: () => void;
  onApply: (filter: SearchInputs<GraphQL.CustomerProductsQueryVariables>) => void;
};
export function CustomerProductFilterInner() {
  const { classes } = useFilterStyles();
  const { filters, onClose, onApply, organizations } = useOutletContext<InnerProps>();
  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer:
        filters?.find((value) => value?.field === GraphQL.CustomerProductSearchField.CustomerPublicId)?.firstValue ??
        NO_FILTER_VALUE,
    },
  });
  const [orgSearch, setOrgSearch] = useState<string>("");

  const handleApply = (customerPublicId: string) => {
    if (customerPublicId === NO_FILTER_VALUE) {
      onApply([]);
    } else {
      const filter: GraphQL.CustomerProductSearchInput = {
        field: GraphQL.CustomerProductSearchField.CustomerPublicId,
        condition: GraphQL.SearchCondition.Equals,
        firstValue: customerPublicId,
      };
      onApply([filter]);
    }
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
  };

  return (
    <Stack className={classes.root}>
      <form
        id="customer-product-filter-form"
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        <RpaDropdown
          label="Organizations"
          data={toSelectionData(organizations)}
          searchable
          searchValue={orgSearch}
          onSearchChange={setOrgSearch}
          onDropdownOpen={() => setOrgSearch("")}
          {...getInputProps("selectedCustomer")}
        />
      </form>
    </Stack>
  );
}

function toSelectionData(
  organizations?: GraphQL.AdminSearchCustomersForFilterQuery["adminSearchCustomers"]["data"]
): SelectItem[] {
  const customersAsSelectData =
    organizations?.map<SelectItem>((customer) => ({
      label: customer.organizationName ? `${customer.organizationName} (${customer.publicId})` : customer.publicId,
      value: customer.publicId,
    })) ?? [];
  const all: SelectItem = { label: "All", value: NO_FILTER_VALUE };
  return [all, ...customersAsSelectData];
}

/**
 * query to paginate through all results for the adminSearchCustomers query
 * this allows us to get all customers for frontend 'search' without having to query the backend on user input
 * or allow an unbounded query in the backend
 * @returns A list of all customers
 */
export function useAllCustomers(enabled: boolean = true) {
  const graphqlRequest = useRawGraphqlRequest();
  const customersQuery = useQuery({
    queryKey: ["admin-list-all-customers"],
    queryFn: async () => {
      const allCustomers: GraphQL.AdminSearchCustomersForFilterQuery["adminSearchCustomers"]["data"] = [];
      let remainingCustomers = 0;
      let nextOffset = 0;
      do {
        const { adminSearchCustomers } = await graphqlRequest<
          GraphQL.AdminSearchCustomersForFilterQuery,
          GraphQL.AdminSearchCustomersForFilterQueryVariables
        >(GraphQL.AdminSearchCustomersForFilterDocument, {
          limit: 1000,
          offset: nextOffset,
        });

        if (
          !adminSearchCustomers.nextOffset ||
          !adminSearchCustomers.totalCount ||
          adminSearchCustomers.data.length === 0
        ) {
          remainingCustomers = 0;
        } else {
          nextOffset = adminSearchCustomers.nextOffset;
          allCustomers.push(...adminSearchCustomers.data);
          remainingCustomers = adminSearchCustomers.totalCount - allCustomers.length;
        }
      } while (remainingCustomers > 0);

      return allCustomers;
    },
    enabled,
  });
  return customersQuery.data;
}
